import { useAppSelector } from '../store/hooks';

/**
 * Exposes helper methods to check what the logged-in user is allowed to do,
 * based on the `permissions` array returned by the login API.
 *
 * Permission string formats:
 *   "Module.SubModule.action"  – e.g. "Dashboard.Activity.read"
 *   "Module.FullAccess"        – e.g. "Teams.FullAccess"
 */
export const usePermissions = () => {
  const user = useAppSelector((state) => state.auth.user);
  const permissions: string[] = user.permissions;
  const isSuperAdmin = user.isSuperAdmin;
  const isAdmin = user.isAdmin;

  const hasAtleastAdminAccess = (): boolean => {
    if (isSuperAdmin || isAdmin) return true;

    return false;
  };

  /**
   * Returns true if user has access to module (and optional submodule),
   * case-insensitive.
   *
   * Matches either:
   *   - module scope:    "Module.*"
   *   - module wildcard: "Module.FullAccess"
   *   - submodule scope: "Module.SubModule.*" (when `subModule` provided)
   *
   * Used for sidebar visibility — hide item when false.
   */
  const hasModuleAccess = (module: string | undefined, subModule?: string): boolean => {
    if (isSuperAdmin) return true;
    if (!module) return true; // no restriction defined → always show
    const moduleKey = module.toLowerCase();
    const subModuleKey = subModule?.toLowerCase();

    return permissions.some((permission) => {
      const p = permission.toLowerCase();

      if (p === `${moduleKey}.fullaccess`) return true;
      if (!p.startsWith(moduleKey + '.')) return false;

      if (!subModuleKey) return true;

      return p.startsWith(`${moduleKey}.${subModuleKey}.`);
    });
  };

  /**
   * Returns true when the user can perform `action` on `Module.SubModule`.
   *
   * Matches either:
   *   - exact  "Module.SubModule.action"
   *   - wildcard "Module.FullAccess"
   *
   * Used for button disabled state — disable when false.
   */
  const hasAction = (module: string, subModule: string, action: string): boolean => {
    if (isSuperAdmin) return true;
    const exact = `${module}.${subModule}.${action}`.toLowerCase();
    const fullAccess = `${module}.FullAccess`.toLowerCase();
    return permissions.some((p) => p.toLowerCase() === exact || p.toLowerCase() === fullAccess);
  };

  /**
   * Convenience: true when the module has FullAccess.
   */
  const hasFullAccess = (module: string): boolean => {
    if (isSuperAdmin) return true;
    const fullAccess = `${module}.FullAccess`.toLowerCase();
    return permissions.some((p) => p.toLowerCase() === fullAccess);
  };

  return {
    hasModuleAccess,
    hasAction,
    hasFullAccess,
    permissions,
    isSuperAdmin,
    hasAtleastAdminAccess,
  };
};
